import React from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	CloseIcon,
	layout,
	palette,
	typography,
} from '@citrite/web-ui-component';
import styled from '@emotion/styled';

interface FilterOption {
	id: string;
	label: string;
	enabled: boolean;
	icon?: React.ReactNode;
}

interface FilterPopupProps {
	filters: FilterOption[];
	onFilterChange: (filterId: string, enabled: boolean) => void;
	onClose?: () => void;
}

const StyledFilterCard = styled.div`
	background: ${palette.white};
	border-radius: 8px;
	border: 1px solid ${palette.grey300};
	width: 268px;
	height: 212px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.24);
`;

const StyledHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: ${layout.mediumSpace} ${layout.largeSpace};
	border-bottom: 1px solid ${palette.grey200};
`;

const StyledTitle = styled.h3`
	font-size: ${typography.sizes.base};
	font-weight: ${typography.weights.semiBold};
	color: ${palette.grey900};
	margin: 0;
`;

const StyledCloseButton = styled(Button.IconOnly)`
	padding: 4px;
	border: none;
	background: none;
	cursor: pointer;

	&:hover {
		background-color: ${palette.grey100};
	}
`;

const StyledBody = styled.div`
	padding: ${layout.mediumSpace} ${layout.largeSpace};
`;

const StyledFilterOption = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${layout.smallSpace};
	padding: ${layout.smallSpace} 0;
`;

const StyledFilterLeft = styled.div`
	display: flex;
	align-items: center;
	gap: ${layout.smallSpace};
`;

const StyledLabel = styled.span`
	font-size: ${typography.sizes.sml};
	color: ${palette.grey900};
	font-weight: ${typography.weights.regular};
`;

const StyledSectionTitle = styled.div`
	font-weight: ${typography.weights.semiBold};
	font-size: ${typography.sizes.sml};
	color: ${palette.grey700};
	padding: ${layout.smallSpace} 0;
`;

// Custom Toggle Switch Component
const ToggleWrapper = styled.div`
	display: inline-block;
	position: relative;
`;

const ToggleInput = styled.input`
	opacity: 0;
	width: 0;
	height: 0;
`;

const ToggleSlider = styled.span<{ checked: boolean }>`
	position: relative;
	display: inline-block;
	width: 44px;
	height: 24px;
	background-color: ${props => (props.checked ? palette.teal600 : palette.grey300)};
	border-radius: 24px;
	transition: 0.2s;
	cursor: pointer;

	&:before {
		position: absolute;
		content: '';
		height: 18px;
		width: 18px;
		left: ${props => (props.checked ? '23px' : '3px')};
		top: 3px;
		background-color: ${palette.white};
		border-radius: 50%;
		transition: 0.2s;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	}

	/* Checkmark when enabled */
	&:after {
		content: ${props => (props.checked ? '"✓"' : '""')};
		position: absolute;
		top: 50%;
		left: ${props => (props.checked ? '12px' : '50%')};
		transform: translate(-50%, -50%);
		color: ${palette.white};
		font-size: 10px;
		font-weight: bold;
		transition: 0.2s;
	}
`;

export const FilterPopup: React.FC<FilterPopupProps> = ({
	filters,
	onFilterChange,
	onClose,
}) => {
	const handleToggleChange = (filterId: string, checked: boolean) => {
		onFilterChange(filterId, checked);
	};

	return (
		<StyledFilterCard>
			<StyledHeader>
				<StyledTitle>{t('Workspace:service_continuity.filters')}</StyledTitle>
				{onClose && (
					<StyledCloseButton
						icon={CloseIcon}
						onClick={onClose}
						size={16}
						title={t('Workspace:service_continuity.close_filters')}
						aria-label={t('Workspace:service_continuity.close_filters')}
					/>
				)}
			</StyledHeader>
			<StyledBody>
				<StyledSectionTitle>
					{t('Workspace:service_continuity.show_lease_status')}
				</StyledSectionTitle>
				{filters.map(filter => (
					<StyledFilterOption key={filter.id}>
						<StyledFilterLeft>
							{filter.icon}
							<StyledLabel>{filter.label}</StyledLabel>
						</StyledFilterLeft>
						<ToggleWrapper>
							<ToggleInput
								type="checkbox"
								checked={filter.enabled}
								onChange={e => handleToggleChange(filter.id, e.target.checked)}
							/>
							<ToggleSlider
								checked={filter.enabled}
								onClick={() => handleToggleChange(filter.id, !filter.enabled)}
							/>
						</ToggleWrapper>
					</StyledFilterOption>
				))}
			</StyledBody>
		</StyledFilterCard>
	);
};
